import { Button, Modal } from '@components/common';
import { FormInput, FormSelect } from '@components/common/FormField';
import React, { useState } from 'react';
import {
  AccountNormalBalance,
  AccountRecord,
  AccountType,
} from '../../../../types/accounting';
import {
  ACCOUNT_TYPE_OPTIONS,
  AccountFormState,
  NORMAL_BALANCE_OPTIONS,
} from './accountForm';

interface AccountEditorModalProps {
  initial: AccountFormState;
  accounts: AccountRecord[];
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: AccountFormState) => void;
}

export function AccountEditorModal({
  initial,
  accounts,
  isSaving,
  title,
  onClose,
  onSubmit,
}: AccountEditorModalProps) {
  const [form, setForm] = useState<AccountFormState>(initial);

  const set = <K extends keyof AccountFormState>(
    key: K,
    value: AccountFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Account code"
            required
            value={form.code}
            onChange={(event) => set('code', event.target.value)}
            placeholder="e.g. 1000"
          />
          <FormInput
            label="Account name"
            required
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="e.g. Cash on Hand"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormSelect
            label="Account type"
            required
            value={form.type}
            onChange={(event) => set('type', event.target.value as AccountType)}
          >
            {ACCOUNT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Normal balance"
            required
            value={form.normalBalance}
            onChange={(event) =>
              set('normalBalance', event.target.value as AccountNormalBalance)
            }
          >
            {NORMAL_BALANCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Subtype"
            value={form.subtype}
            onChange={(event) => set('subtype', event.target.value)}
            placeholder="Optional subtype"
          />
          <FormSelect
            label="Parent account"
            value={form.parentAccountId}
            onChange={(event) => set('parentAccountId', event.target.value)}
          >
            <option value="">No parent account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} - {account.name}
              </option>
            ))}
          </FormSelect>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => set('isActive', event.target.checked)}
            />
            Account is active
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.allowsManualEntries}
              onChange={(event) =>
                set('allowsManualEntries', event.target.checked)
              }
            />
            Allow manual entries
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save account
          </Button>
        </div>
      </form>
    </Modal>
  );
}
